import { useState } from "react";
import axios from "axios";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API, errorText } from "@/lib/payments";

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-medium text-stone-700 mb-2 block">{label}</label>
    <Input className="h-12" required {...props} />
  </div>
);

export const AdminLogin = ({ onLoggedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(errorText(err, "Sign-in failed"));
      if (err?.response?.status === 401 && mfaToken && /expired/i.test(errorText(err, ""))) setMfaToken(null);
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.mfa_required) setMfaToken(data.mfa_token);
      else onLoggedIn(data.user);
    });
  };

  const submitCode = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await axios.post(`${API}/auth/login/verify`, { mfa_token: mfaToken, code });
      setPassword("");
      onLoggedIn(data.user, data.used_recovery_code);
    });
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-sand-200/30 pt-32 md:pt-40 lg:pt-48 pb-20">
      <div className="container-custom"><div className="max-w-md mx-auto">
        <Card className="border-stone-200">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              {mfaToken ? <ShieldCheck className="h-8 w-8 text-primary" strokeWidth={1.5} /> : <Lock className="h-8 w-8 text-primary" strokeWidth={1.5} />}
            </div>
            <CardTitle className="font-heading text-2xl">{mfaToken ? "Two-step verification" : "Admin Access"}</CardTitle>
          </CardHeader>
          <CardContent>
            {!mfaToken ? (
              <form onSubmit={submitPassword} className="space-y-4">
                <Field label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
                <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
                {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                <Button type="submit" data-testid="admin-login-btn" disabled={busy} className="w-full rounded-full py-6">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submitCode} className="space-y-4">
                <p className="text-sm text-stone-600">Enter the 6-digit code from your authenticator app, or one of your recovery codes.</p>
                <Field label="Code" autoComplete="one-time-code" inputMode="text" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="admin-mfa-code-input" />
                {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                <Button type="submit" data-testid="admin-mfa-verify-btn" disabled={busy} className="w-full rounded-full py-6">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify and sign in"}
                </Button>
                <button type="button" className="text-sm text-stone-500 underline w-full" onClick={() => { setMfaToken(null); setCode(""); }} data-testid="admin-mfa-back-btn">Back</button>
              </form>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};
